import React, { useRef, useEffect, useState, useCallback } from 'react'

import ReactDOMServer from 'react-dom/server'

import icons from '../assets/icons'

import '../styles/gm-infowindow.css'
import useLanguage from '../hooks/useLanguage'
import useShips from '../hooks/useShips'
import ModalInfo from './ModalInfo'
import useStopovers from '../hooks/useStopovers'
import Cruise, { CruiseEscale } from '../types/Cruise'
import Stopover from '../types/Stopover'
import Ship from '../types/Ship'
import mapStyle from '../styles/mapsStyle'
import Pickers from './Pickers'
import useCluster from '../hooks/useCluster'
import CruiseLayer from './CruiseLayer'
import MapCruiseModal from './MapCruiseModal'
import { cruisePaths } from '../utils/markers'
import { useTranslation } from 'react-i18next'
import { RotatingLines } from 'react-loader-spinner'
import BrandingLayer from './BrandingLayer'
import { unCapitalize } from '../utils/string'

type Props = google.maps.MapOptions

enum infoType {
  BOAT,
  STOP,
  CRUISE,
}

const queryParams = new URLSearchParams(window.location.search)
const shipCode = queryParams.get('shipCode')
const branding = queryParams.get('branding')

const MapComponent: React.FC<Props> = ({ zoom, center }: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map>()
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow>()
  const [escalesmarkers, setEscalesMarkers] = useState<{ [key: string]: google.maps.Marker }>({})
  const [shipMarkers, setShipMarkers] = useState<{ [key: string]: google.maps.Marker }>({})
  const [cruiseMarkers, setCruiseMarkers] = useState<Array<google.maps.Marker>>([])
  const [cruise, setCruise] = useState<Cruise | null>(null)
  const [item, setItem] = useState<any | null>(null)
  const [selectedMarker, setSelectedMarker] = useState<{
    marker: google.maps.Marker
    icon: string | google.maps.Icon
  } | null>(null)
  const { cluster, createCluster, shipCluster, createShipCluster } = useCluster()
  const [showCruiseLayer, setShowCruiseLayer] = useState(false)
  const { t } = useTranslation()

  const language = useLanguage()
  const { ships, hasError: hasErrorShip } = useShips()
  const { stopovers, hasError: hasErrorStop } = useStopovers()

  const contentString = useCallback(
    (item: any, type: infoType) => {
      if (type === infoType.STOP || type === infoType.CRUISE) {
        return (
          <div id={`infowindow-${item.port_id}`} className='marker--infoContainer'>
            {item.port_images[language] ? (
              <>
                {/* <img className='marker--image' src={item.port_images[language]} alt={item.port_id} /> */}
                <img
                  className='marker--image'
                  src={`${item.port_images[language]}`}
                  alt={item.port_id}
                />
              </>
            ) : (
              <div className='marker--image text text--weight400 text--size13 text--center text--black'>
                {t('cruise.noImage')}
              </div>
            )}
            <div className='marker--infoSection marker--infoSection--stop'>
              <div className='text text--size20 text--black'>
                {unCapitalize(item.port_name[language])}
              </div>
            </div>
            <img src={icons['arrow-right']} className='marker--infoSection--arrow' alt='arrow' />
          </div>
        )
      }
      return (
        <div id={`infowindow-${item.code}`} className='marker--infoContainer'>
          {item.ship_images[language] ? (
            <img
              id={item.code}
              className='marker--image'
              src={`${item.ship_images[language]}`}
              alt={item.code}
            />
          ) : (
            <div className='marker--image text text--weight400 text--size13 text--center text--black'>
              {t('cruise.noImage')}
            </div>
          )}

          <div className='marker--infoSection marker--infoSection--ship'>
            {/* {item.cruise_current.length !== 0 && (
              <CruisingStatus />
            )} */}
            <div className='marker--title text text--size20 text--black'>
              {unCapitalize(item.name)}
            </div>
            <div className='text text--size14 text--gray'>
              {item.infos[language]}
            </div>
          </div>
          <img src={icons['arrow-right']} className='marker--infoSection--arrow' alt='arrow' />
        </div>
      )
    },
    [language, t],
  )

  const updateIcon = useCallback((type: infoType, marker: google.maps.Marker) => {
    if (type === infoType.BOAT) {
      marker.setIcon(icons['selected-ship'])
    }
    if (type === infoType.STOP) {
      marker.setIcon(icons['selected-point'])
    }
  }, [])

  const onPickShip = useCallback(
    (ship: Ship) => {
      map?.setZoom(6)
      map?.panTo({ lat: ship.latitude, lng: ship.longitude })
      const marker = shipMarkers[ship.code]
      if (marker) {
        setSelectedMarker((prevState) => {
          if (prevState && prevState.marker) {
            prevState.marker.setIcon(prevState.icon)
          }
          return { marker, icon: icons.ship }
        })
        updateIcon(infoType.BOAT, marker)
        if (window.innerWidth > 1023) {
          showInfoWindow(ship, infoType.BOAT, marker)
        }
      }
    },
    [map, shipMarkers, updateIcon],
  )

  const onPickStop = useCallback(
    (stopover: Stopover) => {
      map?.setZoom(8)
      map?.panTo({ lat: stopover.latitude, lng: stopover.longitude })

      const marker = escalesmarkers[stopover.port_id]
      if (marker) {
        setSelectedMarker((prevState) => {
          if (prevState && prevState.marker) {
            prevState.marker.setIcon(prevState.icon)
          }
          return { marker, icon: icons.point }
        })
        updateIcon(infoType.STOP, marker)
      }
      if (window.innerWidth > 1024) {
        setItem(stopover)
      } else {
        showInfoWindow(stopover, infoType.STOP, marker)
      }
    },
    [map, escalesmarkers, updateIcon],
  )

  useEffect(() => {
    if (shipCode !== null) {
      const ship = ships.find((item) => item.code === shipCode)
      if (ship) {
        onPickShip(ship)
      }
    }
  }, [onPickShip, ships, shipCode])

  const cleanItem = useCallback(() => {
    setItem(null)
  }, [])

  const onPressItem = useCallback(
    (value: Ship | Stopover | CruiseEscale) => {
      infoWindow?.close()
      const location = { lat: value.latitude, lng: value.longitude }
      map?.panTo(location)
      setItem(value)
    },
    [infoWindow, map],
  )

  const showInfoWindow = useCallback(
    (item: Ship | Stopover | CruiseEscale, type: infoType, marker?: google.maps.Marker) => {
      if (infoWindow) {
        infoWindow.setOptions({
          pixelOffset:
            type === infoType.BOAT || type === infoType.CRUISE
              ? new google.maps.Size(0, -30)
              : new google.maps.Size(0, -10),
        })
        infoWindow.setContent(ReactDOMServer.renderToStaticMarkup(contentString(item, type)))
        infoWindow.setPosition({ lat: item.latitude, lng: item.longitude })
        infoWindow.open(map)
        infoWindow.addListener('visible', () => {
          const id =
            type === infoType.BOAT ? (item as Ship).code : (item as Stopover | CruiseEscale).port_id
          document.getElementById(`infowindow-${id}`)?.addEventListener('click', () => {
            if (marker) {
              setSelectedMarker((prevState) => {
                if (prevState) {
                  prevState.marker.setIcon(prevState.icon)
                }
                const icon =
                  type === infoType.BOAT
                    ? icons.ship
                    : type === infoType.STOP
                    ? icons.point
                    : {
                        url: `data:image/svg+xml;base64,${
                          cruisePaths.SELECTED[(item as CruiseEscale).status]
                        }`,
                        size: new google.maps.Size(46, 46),
                      }
                return { marker, icon }
              })
              updateIcon(type, marker)
            }
            onPressItem(item)
          })
        })
      }
    },
    [contentString, infoWindow, map, onPressItem, updateIcon],
  )

  const createMarker = useCallback(
    (value: Ship | Stopover | CruiseEscale, type: infoType) => {
      const marker = new google.maps.Marker({
        position: { lat: value.latitude, lng: value.longitude },
        clickable: true,
        icon: type === infoType.BOAT ? icons.ship : icons.point,
        map,
      })
      marker.addListener('mouseover', () => {
        if (window.innerWidth > 1023) {
          showInfoWindow(value, type, marker)
        }
      })

      marker.addListener('click', () => {
        setSelectedMarker((prevState) => {
          if (prevState) {
            prevState.marker.setIcon(prevState.icon)
          }
          return { marker, icon: type === infoType.BOAT ? icons.ship : icons.point }
        })
        updateIcon(type, marker)
        onPressItem(value)
      })
      return marker
    },
    [map, onPressItem, showInfoWindow, updateIcon],
  )

  const addShips = useCallback(() => {
    if (Object.values(shipMarkers).length === 0 && map) {
      const obj: { [key: string]: google.maps.Marker } = {}
      const markers = ships.map((value) => {
        const marker = createMarker(value, infoType.BOAT)
        obj[value.code] = marker
        return marker
      })
      setShipMarkers(obj)
      createShipCluster(markers, map)
    }
  }, [createMarker, createShipCluster, map, shipMarkers, ships])

  const addStops = useCallback(() => {
    if (Object.values(escalesmarkers).length === 0 && map) {
      const obj: { [key: string]: google.maps.Marker } = {}
      const markers = stopovers.map((value) => {
        const stop = createMarker(value, infoType.STOP)
        obj[value.port_id] = stop
        return stop
      })
      setEscalesMarkers(obj)
      createCluster(markers, map)
    }
  }, [createCluster, createMarker, escalesmarkers, map, stopovers])

  useEffect(() => {
    if (!hasErrorShip) {
      addShips()
    }
    if (!hasErrorStop) {
      addStops()
    }
  }, [addShips, addStops, hasErrorShip, hasErrorStop]) // component did mount

  useEffect(() => {
    if (ref.current && !map) {
      setMap(
        new window.google.maps.Map(ref.current, {
          zoom,
          center,
          disableDefaultUI: false,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: window.innerWidth > 1023, // show zoom control only on desktop
          zoomControlOptions: { position: google.maps.ControlPosition.BOTTOM_LEFT },
          minZoom: 2.5,
          styles: mapStyle,
        }),
      )
      setInfoWindow(new google.maps.InfoWindow())
    }
  }, [map, zoom, center])

  const removeCruise = useCallback(() => {
    cruiseMarkers.forEach((item) => {
      item.setMap(null)
      item.setVisible(false)
    })
    setCruiseMarkers([])
    setCruise(null)

    if (map) {
      cluster?.addMarkers(Object.values(escalesmarkers))
      shipCluster?.addMarkers(Object.values(shipMarkers))
    }
  }, [cluster, cruiseMarkers, escalesmarkers, map, shipCluster, shipMarkers])

  const createCruiseMarker = useCallback(
    (value: CruiseEscale) => {
      const marker = new google.maps.Marker({
        position: { lat: value.latitude, lng: value.longitude },
        icon: {
          url: `data:image/svg+xml;base64,${cruisePaths[value.status]}`,
          size: new google.maps.Size(30, 30),
        },
        label: {
          text: value.step.toString(),
          color: 'white',
          fontFamily: 'Lato',
          fontSize: '12px',
          fontWeight: '700',
        },
        clickable: true,
        map,
        zIndex: value.step,
      })
      marker.addListener('mouseover', () => {
        if (window.innerWidth > 1023) {
          showInfoWindow(value, infoType.CRUISE, marker)
        }
      })
      marker.addListener('click', () => {
        setSelectedMarker((prevState) => {
          if (prevState) {
            if (!(prevState.icon === icons.ship && cruise !== null)) {
              prevState?.marker.setIcon(prevState.icon)
            }
          }
          return {
            marker,
            icon: {
              url: `data:image/svg+xml;base64,${cruisePaths[value.status]}`,
              size: new google.maps.Size(30, 30),
            },
          }
        })
        marker.setIcon({
          url: `data:image/svg+xml;base64,${cruisePaths.SELECTED[value.status]}`,
          size: new google.maps.Size(46, 46),
        })

        onPressItem(value)
      })
      return marker
    },
    [cruise, map, onPressItem, showInfoWindow],
  )

  const showCruise = useCallback(
    (cruise: Cruise) => {
      infoWindow?.close()
      setShowCruiseLayer(true)
      setCruise(cruise)
      const shipCode = cruise.ship_id
      setShowCruiseLayer(true)
      shipCluster?.clearMarkers()
      cluster?.clearMarkers()
      const bounds = new google.maps.LatLngBounds()

      bounds.extend(shipMarkers[shipCode].getPosition()!)
      shipMarkers[shipCode].setMap(map!)
      shipMarkers[shipCode].setVisible(true)
      shipMarkers[shipCode].setIcon(icons.ship)
      setCruiseMarkers(
        cruise.cruise_escales?.map((value) => {
          const marker = createCruiseMarker(value)
          bounds.extend({ lat: value.latitude, lng: value.longitude })
          return marker
        }) || [],
      )
      map?.fitBounds(bounds)
    },
    [cluster, createCruiseMarker, infoWindow, map, shipCluster, shipMarkers],
  )

  const onCloseModal = useCallback(() => {
    if ((item as Ship).code) {
      selectedMarker?.marker.setIcon(icons.ship)
    }

    if ((item as Stopover).port_id) {
      if ((item as CruiseEscale).status) {
        selectedMarker?.marker.setIcon({
          url: `data:image/svg+xml;base64,${cruisePaths[(item as CruiseEscale).status]}`,
          size: new google.maps.Size(30, 30),
        })
      } else {
        selectedMarker?.marker.setIcon(icons.point)
      }
    }
    setItem(null)
  }, [item, selectedMarker])

  return (
    <React.Fragment>
      {branding === 'true' && <BrandingLayer />}

      {branding === 'true' && (
        <div className='header'>
          <img
            src={icons.logo}
            width={150}
            height={30}
            alt='Logo Ponant'
            className='header--logo'
          />
        </div>
      )}
      <div className={branding === 'true' ? 'container container--brandingHeight' : 'container'}>
        <Pickers
          ships={ships}
          visible={item === null && cruise === null}
          map={map}
          onPickShip={onPickShip}
          onPickStop={onPickStop}
        />
        {cruise !== null && !showCruiseLayer && (
          <MapCruiseModal cruise={cruise} onPressQuit={removeCruise} />
        )}
        <CruiseLayer
          visible={showCruiseLayer}
          cruise={cruise}
          hideLayer={() => setShowCruiseLayer(false)}
        />
        <div ref={ref} id='map' className={item !== null ? 'splitted' : 'splitted--not'} />
        <ModalInfo
          item={item}
          closeModal={onCloseModal}
          setCruise={showCruise}
          cleanItem={cleanItem}
        />
      </div>
    </React.Fragment>
  )
}

export default MapComponent
